import { If, onCleanup, onMount, watch } from 'cdui-js';
import { Steps } from '../../../components/Steps';
import { pState } from '../store';
import i18n from '../../../../../i18n';
import { replaceTemplate } from '@china-difi/lib/template';
import { getEffectiveSafeBottom } from '../../../../../stores/common';
import { keyboardAvoidState, setKeyboardAvoidScrollRoot } from '../keyboardAvoid';
import { notifyOpenProgressReady } from '../notifyOpenProgressReady';

import { SelectIDCard } from './infomations/SelectIDCard';
import { UploadLic } from './infomations/UploadLic';
import { UploadBank } from './infomations/UploadBank';
import { Address } from './infomations/Address';
import { JobStatus } from './infomations/JobStatus';
import { FinancialStatus } from './infomations/FinancialStatus';
import { InvestmentExp } from './infomations/InvestmentExp';
import { TaxInfo } from './infomations/TaxInfo';
import { Others } from './infomations/Others';
import { ActivateEleServices } from './infomations/ActivateEleServices';
import { SelectAccount } from './infomations/SelectAccount';
import { RiskInfo } from './risk/RiskInfo';
import { ConfirmInfos } from './risk/ConfirmInfos';
import { ConfirmAndSign } from './risk/ConfirmAndSign';
import { Signature } from './transfer/Signature';
import { TransferIntro } from './transfer/TransferIntro';
import { SelectCurrency } from './transfer/SelectCurrency';
import { AddBankAccount } from './transfer/AddBankAccount';
import { BankInfoConfirm } from './transfer/BankInfoConfirm';
import { Reviewing } from './transfer/Reviewing';

/** 与 LastNextBtns 底栏高度一致：80 + 底部安全区 */
const LAST_NEXT_BAR_BODY = 80;
const lastNextBarReservePx = () => LAST_NEXT_BAR_BODY + getEffectiveSafeBottom();

/** 大陆：有银联页；资料/风险/入金同包（forge 仍动态 open-cert） */
const CompMaps = {
  2: SelectIDCard,
  3: UploadLic,
  4: UploadBank,
  5: Address,
  6: JobStatus,
  7: FinancialStatus,
  8: InvestmentExp,
  9: TaxInfo,
  10: Others,
  11: ActivateEleServices,
  12: SelectAccount,
  13: RiskInfo,
  14: ConfirmInfos,
  15: ConfirmAndSign,
  16: Signature,
  17: TransferIntro,
  18: SelectCurrency,
  19: AddBankAccount,
  20: BankInfoConfirm,
  21: Reviewing,
};

/** 非大陆（含香港）：无银联四要素页 */
const CompMaps2 = {
  2: SelectIDCard,
  3: UploadLic,
  4: Address,
  5: JobStatus,
  6: FinancialStatus,
  7: InvestmentExp,
  8: TaxInfo,
  9: Others,
  10: ActivateEleServices,
  11: SelectAccount,
  12: RiskInfo,
  13: ConfirmInfos,
  14: ConfirmAndSign,
  15: Signature,
  16: TransferIntro,
  17: SelectCurrency,
  18: AddBankAccount,
  19: BankInfoConfirm,
  20: Reviewing,
};

/** 顶部阶段 / 资料计数边界（与 CompMaps 对齐；不含选证件页） */
const getDisplayBounds = (mainland: boolean) =>
  mainland
    ? { riskStart: 13, transferStart: 17, infoTotal: 10 }
    : { riskStart: 12, transferStart: 16, infoTotal: 9 };

export const OnlineProgress = () => {
  const isMainland = () => pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';
  let lastReadyStep = -1;

  const bounds = () => getDisplayBounds(isMainland());

  const compList = () => (isMainland() ? CompMaps : CompMaps2);

  const step = () => {
    const { riskStart, transferStart } = bounds();
    if (pState.current < riskStart) return 0;
    if (pState.current < transferStart) return 1;
    return 2;
  };

  const hideSteps = () => (isMainland() ? [2, 16] : [2, 15]);

  const stepText = () =>
    replaceTemplate(i18n.OpenProgress.Common.StepCounter, pState.current - 2, bounds().infoTotal);

  const isSignatureStep = () => (isMainland() ? pState.current === 16 : pState.current === 15);
  /** 仅手写签名页脱离 scroll-root（需锁滚/全屏画布）；确认信息并签署须保留可滚 */
  const isOutsideScroll = () => {
    if (isMainland()) return pState.current === 16;
    return pState.current === 15;
  };

  const signalStepReady = (cur: number) => {
    if (cur <= 1 || cur === lastReadyStep) return;
    lastReadyStep = cur;
    notifyOpenProgressReady(cur);
  };

  onMount(() => {
    signalStepReady(pState.current);
  });

  watch(
    () => pState.current,
    (cur) => {
      signalStepReady(cur);
    },
  );

  const StepView = () => {
    const maps = compList() as Record<number, any>;
    const Comp = maps[pState.current];
    if (!Comp) return null;
    return <Comp stepText={stepText()} />;
  };

  onCleanup(() => {
    setKeyboardAvoidScrollRoot(null);
  });

  return (
    <div
      style={{
        height: '100%',
        ...(isSignatureStep()
          ? { overflow: 'hidden', 'touch-action': 'none', 'overscroll-behavior': 'none' }
          : {}),
      }}
    >
      <If when={!isOutsideScroll()}>
        <div
          class="open-progress-scroll-root"
          ref={(el: HTMLDivElement | null) => {
            setKeyboardAvoidScrollRoot(el);
          }}
          style={{
            // 键盘打开时吃满壳高（底栏已藏）；仅在 keyboardOpen 翻转时改一次高度，
            // 配合 keyboardAvoid 的 iOS settle 补滚，避免底部 input 被挡。
            height: keyboardAvoidState.keyboardOpen
              ? '100%'
              : `calc(100% - ${lastNextBarReservePx()}px)`,
            'overflow-y': 'auto',
            '-webkit-overflow-scrolling': 'touch',
          }}
        >
          {!hideSteps().includes(pState.current) && (
            <div class="flex justify-center padding-t-XL">
              <Steps
                value={step()}
                data={[
                  { label: i18n.OpenProgress.Steps.Info, value: 0 },
                  { label: i18n.OpenProgress.Steps.Risk, value: 1 },
                  { label: i18n.OpenProgress.Steps.Transfer, value: 2 },
                ]}
              />
            </div>
          )}

          <div>
            <StepView />
          </div>
        </div>
      </If>
      <If when={isOutsideScroll()}>
        <StepView />
      </If>
    </div>
  );
};
